import deepEqual from 'deep-equal';
import { FestivalData } from '../../../../model/FestivalData';
import { currentLocale, currentMessages } from '../../../../i18n/I18n';

export type RevisionChangeKind = 'added' | 'removed' | 'changed' | 'reordered';

export interface RevisionChange {
  kind: RevisionChangeKind;
  label: string;
  previousValue?: string;
  newValue?: string;
}

type Item = Record<string, unknown> & { identifier: string };

// Every save bumps it, so it never tells what the save changed
const IGNORED_KEYS = ['updated_at'];
const MAX_VALUE_LENGTH = 80;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isItem = (value: unknown): value is Item =>
  isPlainObject(value) && typeof value.identifier === 'string';

/**
 * A value before and after a change, each cut to MAX_VALUE_LENGTH. When they differ beyond what that shows (an
 * edit at the end of a long text), both start a little before the difference, so the change is visible.
 */
const changePreviews = (beforeValue: unknown, afterValue: unknown): [string, string] => {
  const nothing = currentMessages('history').nothing;
  const texts = [JSON.stringify(beforeValue) ?? nothing, JSON.stringify(afterValue) ?? nothing];
  let differsAt = 0;
  while (differsAt < texts[0].length && texts[0][differsAt] === texts[1][differsAt]) differsAt++;
  const from = differsAt < MAX_VALUE_LENGTH - 10 ? 0 : differsAt - 20;
  const [before, after] = texts.map(text =>
    `${from > 0 ? '…' : ''}${text.slice(from, from + MAX_VALUE_LENGTH)}${text.length > from + MAX_VALUE_LENGTH ? '…' : ''}`);
  return [before, after];
};

const formatShowDate = (date: unknown): string =>
  typeof date === 'string'
    ? new Intl.DateTimeFormat(currentLocale(), { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(date))
    : '';

/**
 * Lists what a save changed, comparing the data before and after it. Artists, POIs, shows and maps are matched
 * by identifier, so removing an item from the middle of a list reads as one removal rather than shifted items.
 */
export const describeRevisionChanges = (before: FestivalData, after: FestivalData): RevisionChange[] => {
  const changes: RevisionChange[] = [];
  // An artist removed by the save is only in the data before it, but its shows may still need its name
  const artistNames = new Map([...before.artists, ...after.artists].map(artist => [artist.identifier, artist.name]));

  const itemLabel = (item: Item): string => {
    if (typeof item.name === 'string' && item.name) return item.name;
    // Shows have no name of their own
    if (typeof item.artist_id === 'string') {
      return `${artistNames.get(item.artist_id) ?? item.artist_id}, ${formatShowDate(item.start_date)}`;
    }
    return item.identifier;
  };

  const compareItems = (beforeItems: Item[], afterItems: Item[], path: string[]) => {
    const beforeById = new Map(beforeItems.map(item => [item.identifier, item]));
    const afterById = new Map(afterItems.map(item => [item.identifier, item]));

    afterItems
      .filter(item => !beforeById.has(item.identifier))
      .forEach(item => changes.push({ kind: 'added', label: [...path, itemLabel(item)].join(' › ') }));
    beforeItems
      .filter(item => !afterById.has(item.identifier))
      .forEach(item => changes.push({ kind: 'removed', label: [...path, itemLabel(item)].join(' › ') }));
    beforeItems.forEach(item => {
      const afterItem = afterById.get(item.identifier);
      if (afterItem) compare(item, afterItem, [...path, itemLabel(afterItem)]);
    });

    const keptOrderBefore = beforeItems.filter(item => afterById.has(item.identifier)).map(item => item.identifier);
    const keptOrderAfter = afterItems.filter(item => beforeById.has(item.identifier)).map(item => item.identifier);
    if (!deepEqual(keptOrderBefore, keptOrderAfter)) {
      changes.push({ kind: 'reordered', label: path.join(' › ') });
    }
  };

  const compare = (beforeValue: unknown, afterValue: unknown, path: string[]) => {
    if (deepEqual(beforeValue, afterValue)) return;

    if (Array.isArray(beforeValue) && Array.isArray(afterValue)
      && beforeValue.length + afterValue.length > 0
      && [...beforeValue, ...afterValue].every(isItem)) {
      compareItems(beforeValue as Item[], afterValue as Item[], path);
      return;
    }

    if (isPlainObject(beforeValue) && isPlainObject(afterValue)) {
      new Set([...Object.keys(beforeValue), ...Object.keys(afterValue)]).forEach(key => {
        if (path.length === 0 && IGNORED_KEYS.includes(key)) return;
        compare(beforeValue[key], afterValue[key], [...path, key.replace(/_/g, ' ')]);
      });
      return;
    }

    const [previousValue, newValue] = changePreviews(beforeValue, afterValue);
    changes.push({ kind: 'changed', label: path.join(' › '), previousValue, newValue });
  };

  compare(before, after, []);
  return changes;
};
